import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  TextField,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../services/api";

/* ─── helpers ─── */
function formatRupiah(value) {
  if (!value && value !== 0) return "Rp 0";
  const num = parseFloat(value);
  if (num >= 1_000_000_000) return `Rp ${(num / 1_000_000_000).toFixed(1)} M`;
  if (num >= 1_000_000) return `Rp ${(num / 1_000_000).toFixed(1)} Jt`;
  if (num >= 1_000) return `Rp ${(num / 1_000).toFixed(0)} Rb`;
  return `Rp ${num.toLocaleString("id-ID")}`;
}

function extractList(d) {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  return [];
}

/* ─── status maps ─── */
const SARPRAS_STATUS = {
  pending: { label: "Pending", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  disetujui: { label: "Disetujui", color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" },
  ditolak: { label: "Ditolak", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};
const ATK_STATUS = {
  pending: { label: "Pending", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  dibeli: { label: "Dibeli", color: "#059669", bg: "#f0fdf4", border: "#bbf7d0" },
  belum_dibeli: { label: "Belum Dibeli", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

function getStatusStyle(statusName, type = "sarpras") {
  const map = type === "atk" ? ATK_STATUS : SARPRAS_STATUS;
  return (
    map[statusName?.toLowerCase()] || {
      label: statusName || "-",
      color: "#6b7280",
      bg: "#f9fafb",
      border: "#e5e7eb",
    }
  );
}

function StatusBadge({ statusName, type }) {
  const sc = getStatusStyle(statusName, type);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        background: sc.bg,
        border: `1px solid ${sc.border}`,
        borderRadius: "6px",
        px: 1,
        py: 0.3,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", color: sc.color, fontWeight: 600 }}>
        {sc.label}
      </Typography>
    </Box>
  );
}

function TypeBadge({ type }) {
  const isSarpras = type === "sarpras";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: "6px",
        py: "2px",
        borderRadius: "5px",
        backgroundColor: isSarpras ? "#eff6ff" : "#fdf4ff",
        border: `1px solid ${isSarpras ? "#bfdbfe" : "#e9d5ff"}`,
      }}
    >
      {isSarpras ? (
        <LocalAtmOutlinedIcon sx={{ fontSize: 10, color: "#2563eb" }} />
      ) : (
        <EditNoteOutlinedIcon sx={{ fontSize: 10, color: "#9333ea" }} />
      )}
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: isSarpras ? "#2563eb" : "#9333ea",
        }}
      >
        {isSarpras ? "Sarpras" : "ATK"}
      </Typography>
    </Box>
  );
}

/* ═══ Main ═══ */
export default function UserDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  // MUI v7: useMediaQuery API tidak berubah
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [procurements, setProcurements] = useState([]);
  const [atkRequests, setAtkRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("semua"); // semua | sarpras | atk
  const [search, setSearch] = useState("");

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [procRes, atkRes] = await Promise.all([
          api.get("/procurements"),
          api.get("/atk"),
        ]);

        const allProc = extractList(procRes.data);
        const allAtk = extractList(atkRes.data);

        const myProc = allProc.filter(
          (p) =>
            String(p.user_id) === String(user?.id) ||
            String(p.user?.id) === String(user?.id)
        );
        const myAtk = allAtk.filter(
          (a) =>
            String(a.user_id) === String(user?.id) ||
            String(a.user?.id) === String(user?.id)
        );

        setProcurements(myProc);
        setAtkRequests(myAtk);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  /* ── gabungan untuk tabel ── */
  const combined = [
    ...procurements.map((p) => ({ ...p, _type: "sarpras" })),
    ...atkRequests.map((a) => ({ ...a, _type: "atk" })),
  ].sort(
    (a, b) =>
      new Date(b.created_at || b.request_date) -
      new Date(a.created_at || a.request_date)
  );

  const filtered =
    activeTab === "sarpras"
      ? combined.filter((r) => r._type === "sarpras")
      : activeTab === "atk"
      ? combined.filter((r) => r._type === "atk")
      : combined;

  /* ── apply search ── */
  const searched = search.trim()
    ? filtered.filter((r) =>
        r.request_number?.toLowerCase().includes(search.toLowerCase())
      )
    : filtered;

  const recent = searched.slice(0, 5);

  /* ── stat counts ── */
  const totalAll = combined.length;
  const pendingAll = combined.filter(
    (r) => r.status?.name?.toLowerCase() === "pending"
  ).length;
  const approvedAll = combined.filter((r) =>
    ["disetujui", "dibeli"].includes(r.status?.name?.toLowerCase())
  ).length;
  const rejectedAll = combined.filter((r) =>
    ["ditolak", "belum_dibeli"].includes(r.status?.name?.toLowerCase())
  ).length;

  const handleRowClick = (row) => {
    if (row._type === "sarpras") navigate(`/user/procurement/${row.id}`);
    else navigate(`/user/atk/${row.id}`);
  };

  const statCards = [
    {
      label: "Total Pengajuan",
      value: totalAll,
      icon: <AssignmentOutlinedIcon sx={{ fontSize: 17 }} />,
      color: "#1677ff",
      bg: "#eff6ff",
      border: "#bfdbfe",
    },
    {
      label: "Menunggu",
      value: pendingAll,
      icon: <HourglassEmptyOutlinedIcon sx={{ fontSize: 17 }} />,
      color: "#d97706",
      bg: "#fffbeb",
      border: "#fde68a",
    },
    {
      label: "Disetujui",
      value: approvedAll,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 17 }} />,
      color: "#059669",
      bg: "#f0fdf4",
      border: "#bbf7d0",
    },
    {
      label: "Ditolak",
      value: rejectedAll,
      icon: <CancelOutlinedIcon sx={{ fontSize: 17 }} />,
      color: "#dc2626",
      bg: "#fef2f2",
      border: "#fecaca",
    },
  ];

  return (
    <Box>
      {/* ── SEARCH BAR ── */}
      <Box sx={{ mb: 3 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Cari no. pengajuan..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, color: "#9ca3af", mr: 0.8 }} />,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              fontSize: "0.875rem",
              backgroundColor: "#fff",
              "& fieldset": { borderColor: "#e5e7eb" },
              "&:hover fieldset": { borderColor: "#9ca3af" },
              "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: "1.5px" },
            },
          }}
        />
      </Box>

      {/* ── STAT CARDS ── */}
      {/*
        MUI v7 Grid perubahan penting:
        - Tidak ada lagi prop `item` dan `container` yang terpisah
        - Gunakan `size` sebagai pengganti `xs`, `sm`, `md`, dst.
        - `spacing` tetap sama di Grid container
      */}
      <Grid container spacing={1.5} sx={{ mb: 4 }}>
        {statCards.map((card) => (
          // MUI v7: pakai `size` bukan `xs`/`md`
          <Grid key={card.label} size={{ xs: 6, md: 3 }}>
            <Box
              sx={{
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                p: { xs: "14px 12px", sm: "18px 16px" },
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 34, sm: 38 },
                  height: { xs: 34, sm: 38 },
                  borderRadius: "9px",
                  backgroundColor: card.bg,
                  border: `1px solid ${card.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: card.color,
                  flexShrink: 0,
                }}
              >
                {card.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "0.6rem", sm: "0.65rem" },
                    color: "#9ca3af",
                    fontWeight: 500,
                    mb: 0.2,
                  }}
                >
                  {card.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1.3rem", sm: "1.5rem" },
                    fontWeight: 800,
                    color: "#111827",
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {loading ? "—" : card.value}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* ── RECENT TABLE ── */}
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* Header + Tabs */}
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            pt: 2,
            pb: 0,
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827" }}>
                Pengajuan Terbaru
              </Typography>
              <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af", mt: 0.2 }}>
                {loading ? "Memuat..." : `${combined.length} total pengajuan (Sarpras + ATK)`}
              </Typography>
            </Box>
            <Box
              onClick={() =>
                activeTab === "atk"
                  ? navigate("/user/atk")
                  : navigate("/user/procurement")
              }
              sx={{
                fontSize: "0.75rem",
                color: "#6b7280",
                fontWeight: 500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.2,
                mt: 0.5,
                "&:hover": { color: "#111827" },
                transition: "color 0.15s",
              }}
            >
              Lihat Semua <ChevronRightIcon sx={{ fontSize: 14 }} />
            </Box>
          </Box>

          {/* Filter Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              minHeight: 32,
              "& .MuiTabs-indicator": { backgroundColor: "#2563eb", height: 2 },
              "& .MuiTab-root": {
                minHeight: 32,
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#9ca3af",
                px: 1.5,
                py: 0,
                "&.Mui-selected": { color: "#2563eb", fontWeight: 700 },
              },
            }}
          >
            <Tab label={`Semua (${combined.length})`} value="semua" />
            <Tab label={`Sarpras (${procurements.length})`} value="sarpras" />
            <Tab label={`ATK (${atkRequests.length})`} value="atk" />
          </Tabs>
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} sx={{ color: "#d1d5db" }} />
          </Box>
        ) : recent.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <AssignmentOutlinedIcon sx={{ fontSize: 32, color: "#d1d5db", mb: 1.5 }} />
            <Typography
              sx={{ color: "#6b7280", fontSize: "0.845rem", fontWeight: 600, mb: 0.4 }}
            >
              {search ? "Tidak ditemukan." : "Belum ada pengajuan"}
            </Typography>
            {!search && (
              <Typography sx={{ color: "#9ca3af", fontSize: "0.75rem", mb: 2.5 }}>
                Mulai ajukan kebutuhan unit kamu
              </Typography>
            )}
            {!search && (
              <Box
                onClick={() => navigate("/user/procurement/create")}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 2,
                  py: 0.7,
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  color: "#374151",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f9fafb" },
                  transition: "all 0.15s",
                }}
              >
                Buat Sekarang
              </Box>
            )}
          </Box>
        ) : isMobile ? (
          /* ── Mobile ── */
          <Box>
            {recent.map((row, i) => (
              <Box
                key={`${row._type}-${row.id}`}
                onClick={() => handleRowClick(row)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2,
                  py: 1.6,
                  borderBottom: i < recent.length - 1 ? "1px solid #f9fafb" : "none",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#fafafa" },
                  transition: "background 0.15s",
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.3 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#111827" }}>
                      {row.request_number}
                    </Typography>
                    <TypeBadge type={row._type} />
                  </Box>
                  <Typography sx={{ fontSize: "0.65rem", color: "#9ca3af" }}>
                    {row.request_date
                      ? new Date(row.request_date).toLocaleDateString("id-ID", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "-"}
                    {" · "}
                    {row.items?.length ?? 0} item
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  {row._type === "sarpras" && row.total_amount > 0 && (
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#111827", mb: 0.3 }}
                    >
                      {formatRupiah(row.total_amount)}
                    </Typography>
                  )}
                  <StatusBadge statusName={row.status?.name} type={row._type} />
                </Box>
                <ChevronRightIcon sx={{ fontSize: 14, color: "#d1d5db", flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        ) : (
          /* ── Desktop ── */
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.6fr 0.7fr 1fr 0.5fr 1fr 0.9fr",
                gap: 2,
                px: 2.5,
                py: 1.5,
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              {["No. Pengajuan", "Jenis", "Tanggal", "Item", "Total", "Status"].map((h) => (
                <Typography
                  key={h}
                  sx={{
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>
            {recent.map((row, i) => (
              <Box
                key={`${row._type}-${row.id}`}
                onClick={() => handleRowClick(row)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.6fr 0.7fr 1fr 0.5fr 1fr 0.9fr",
                  gap: 2,
                  px: 2.5,
                  py: 1.6,
                  alignItems: "center",
                  borderBottom: i < recent.length - 1 ? "1px solid #f9fafb" : "none",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#fafafa" },
                  transition: "background 0.15s",
                }}
              >
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#111827" }}>
                  {row.request_number}
                </Typography>
                <TypeBadge type={row._type} />
                <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
                  {row.request_date
                    ? new Date(row.request_date).toLocaleDateString("id-ID", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "-"}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#6b7280" }}>
                  {row.items?.length ?? 0}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#111827" }}>
                  {row._type === "sarpras" && row.total_amount > 0 ? (
                    formatRupiah(row.total_amount)
                  ) : (
                    <span style={{ color: "#9ca3af" }}>—</span>
                  )}
                </Typography>
                <StatusBadge statusName={row.status?.name} type={row._type} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
